import React, { useMemo } from 'react';
import './NFTStatsCard.css';
import { POPPER_NFT_LAYOUT } from '../constants/accountLayouts';

// Destructure reserved bytes layout for convenience
const { RESERVED } = POPPER_NFT_LAYOUT;
const { STATS } = RESERVED;

/**
 * NFTStatsCard Component
 * 
 * Displays an NFT's stats in a beautiful radar/star chart format.
 * Shows: Cuteness, Creativity, Pop Power, Originality, Potential
 * Plus: Total Votes and Total Sales
 * 
 * @param {Object} props
 * @param {string} props.name - NFT name
 * @param {string} props.description - NFT description
 * @param {string} props.imageUrl - NFT image URL
 * @param {Object} props.stats - Stats object with averages (0-10 scale)
 * @param {number} props.stats.cuteness - Cuteness rating (0-10)
 * @param {number} props.stats.creativity - Creativity rating (0-10)
 * @param {number} props.stats.popPower - Pop Power rating (0-10)
 * @param {number} props.stats.originality - Originality rating (0-10)
 * @param {number} props.stats.potential - Potential rating (0-10)
 * @param {number} props.totalVotes - Total number of votes
 * @param {number} props.totalSales - Total product sales
 * @param {function} props.onClose - Close handler
 * @param {boolean} props.showCloseButton - Whether to show close button (default true)
 */
const NFTStatsCard = ({ 
  name, 
  description, 
  imageUrl, 
  stats = {}, 
  totalVotes = 0, 
  totalSales = 0,
  onClose,
  showCloseButton = true
}) => {
  // Ensure stats have default values
  const safeStats = {
    cuteness: stats.cuteness || 0,
    creativity: stats.creativity || 0,
    popPower: stats.popPower || 0,
    originality: stats.originality || 0,
    potential: stats.potential || 0,
  };

  // Calculate overall score (average of all stats)
  const overallScore = useMemo(() => {
    const values = Object.values(safeStats);
    const sum = values.reduce((a, b) => a + b, 0);
    return (sum / values.length).toFixed(1);
  }, [safeStats]);

  // Generate radar chart points
  // The radar chart has 5 axes, evenly spaced (72 degrees apart)
  // We use SVG to draw it
  const radarPoints = useMemo(() => {
    const stats_array = [
      safeStats.cuteness,
      safeStats.creativity,
      safeStats.popPower,
      safeStats.originality,
      safeStats.potential,
    ];
    
    const centerX = 100;
    const centerY = 100;
    const maxRadius = 80;
    
    // Generate points for the data polygon
    const points = stats_array.map((value, index) => {
      const angle = (index * 72 - 90) * (Math.PI / 180); // Start from top
      const radius = (value / 10) * maxRadius;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      return `${x},${y}`;
    }).join(' ');
    
    return points;
  }, [safeStats]);

  // Generate axis lines and labels
  const axisData = useMemo(() => {
    const labels = ['Cuteness', 'Creativity', 'Pop Power', 'Originality', 'Potential'];
    const emojis = ['🥰', '🎨', '💥', '✨', '🚀'];
    const values = [
      safeStats.cuteness,
      safeStats.creativity,
      safeStats.popPower,
      safeStats.originality,
      safeStats.potential,
    ];
    
    const centerX = 100;
    const centerY = 100;
    const maxRadius = 80;
    const labelRadius = 95;
    
    return labels.map((label, index) => {
      const angle = (index * 72 - 90) * (Math.PI / 180);
      const endX = centerX + maxRadius * Math.cos(angle);
      const endY = centerY + maxRadius * Math.sin(angle);
      const labelX = centerX + labelRadius * Math.cos(angle);
      const labelY = centerY + labelRadius * Math.sin(angle);
      
      return {
        label,
        emoji: emojis[index],
        value: values[index],
        lineEndX: endX,
        lineEndY: endY,
        labelX,
        labelY,
        angle: index * 72 - 90,
      };
    });
  }, [safeStats]);

  return (
    <div className="nft-stats-card">
      {showCloseButton && onClose && (
        <button className="stats-card-close" onClick={onClose}>×</button>
      )}
      
      <div className="stats-card-header">
        <img src={imageUrl} alt={name} className="stats-card-image" />
        <div className="stats-card-info">
          <h3 className="stats-card-name">{name}</h3>
          {description && (
            <p className="stats-card-description">{description}</p>
          )}
        </div>
      </div>

      <div className="stats-card-chart-container">
        <svg viewBox="0 0 200 200" className="radar-chart">
          {/* Background circles */}
          {[0.25, 0.5, 0.75, 1].map((scale, i) => (
            <circle
              key={i}
              cx="100"
              cy="100"
              r={80 * scale}
              className="radar-circle"
            />
          ))}
          
          {/* Axis lines */}
          {axisData.map((axis, i) => (
            <line
              key={i}
              x1="100"
              y1="100"
              x2={axis.lineEndX}
              y2={axis.lineEndY}
              className="radar-axis"
            />
          ))}
          
          {/* Data polygon */}
          <polygon
            points={radarPoints}
            className="radar-polygon"
          />
          
          {/* Data points */}
          {axisData.map((axis, i) => {
            const angle = (i * 72 - 90) * (Math.PI / 180);
            const radius = (axis.value / 10) * 80;
            const x = 100 + radius * Math.cos(angle);
            const y = 100 + radius * Math.sin(angle);
            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r="4"
                className="radar-point"
              />
            );
          })}
        </svg>
        
        {/* Labels positioned around the chart */}
        <div className="radar-labels">
          {axisData.map((axis, i) => (
            <div
              key={i}
              className={`radar-label radar-label-${i}`}
            >
              <span className="radar-label-emoji">{axis.emoji}</span>
              <span className="radar-label-text">{axis.label}</span>
              <span className="radar-label-value">{axis.value.toFixed(1)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="stats-card-overall">
        <span className="overall-label">Overall Score</span>
        <span className="overall-value">{overallScore}</span>
        <span className="overall-max">/10</span>
      </div>

      <div className="stats-card-metrics">
        <div className="metric">
          <span className="metric-icon">🗳️</span>
          <span className="metric-label">Votes</span>
          <span className="metric-value">{totalVotes.toLocaleString()}</span>
        </div>
        <div className="metric">
          <span className="metric-icon">🛒</span>
          <span className="metric-label">Sales</span>
          <span className="metric-value">{totalSales.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Parse stats from on-chain PopperNFT reserved bytes
 * The on-chain format stores averages scaled by 100 (e.g., 750 = 7.50)
 * 
 * @param {number[]} statAverages - Array of 5 u16 values [cuteness, creativity, popPower, originality, potential]
 * @returns {Object} Stats object with values 0-10
 */
export const parseStatsFromChain = (statAverages) => {
  if (!statAverages || statAverages.length !== 5) {
    return {
      cuteness: 0,
      creativity: 0,
      popPower: 0,
      originality: 0,
      potential: 0,
    };
  }
  
  return {
    cuteness: statAverages[0] / 100,
    creativity: statAverages[1] / 100,
    popPower: statAverages[2] / 100,
    originality: statAverages[3] / 100,
    potential: statAverages[4] / 100,
  };
};

/**
 * Read stats directly from PopperNFT reserved bytes
 * 
 * @param {Uint8Array|number[]} reserved - The 128-byte reserved array from PopperNFT
 * @returns {Object} Stats object with averages (0-10), totals, and stats vote count
 */
export const readStatsFromReserved = (reserved) => {
  if (!reserved || reserved.length < RESERVED.MIN_LENGTH_FOR_STATS) {
    return {
      averages: { cuteness: 0, creativity: 0, popPower: 0, originality: 0, potential: 0 },
      totals: { cuteness: 0, creativity: 0, popPower: 0, originality: 0, potential: 0 },
      statsVoteCount: 0,
    };
  }
  
  // Read averages from reserved bytes (u16 x 5, stored as value * 100)
  // Using offsets from centralized constants
  const averages = {
    cuteness: (reserved[STATS.avgCuteness] | (reserved[STATS.avgCuteness + 1] << 8)) / 100,
    creativity: (reserved[STATS.avgCreativity] | (reserved[STATS.avgCreativity + 1] << 8)) / 100,
    popPower: (reserved[STATS.avgPopPower] | (reserved[STATS.avgPopPower + 1] << 8)) / 100,
    originality: (reserved[STATS.avgOriginality] | (reserved[STATS.avgOriginality + 1] << 8)) / 100,
    potential: (reserved[STATS.avgPotential] | (reserved[STATS.avgPotential + 1] << 8)) / 100,
  };
  
  // Read totals (u16 x 5)
  const totals = {
    cuteness: reserved[STATS.totalCuteness] | (reserved[STATS.totalCuteness + 1] << 8),
    creativity: reserved[STATS.totalCreativity] | (reserved[STATS.totalCreativity + 1] << 8),
    popPower: reserved[STATS.totalPopPower] | (reserved[STATS.totalPopPower + 1] << 8),
    originality: reserved[STATS.totalOriginality] | (reserved[STATS.totalOriginality + 1] << 8),
    potential: reserved[STATS.totalPotential] | (reserved[STATS.totalPotential + 1] << 8),
  };
  
  // Read stats vote count (u16)
  const statsVoteCount = reserved[STATS.statsVoteCount] | (reserved[STATS.statsVoteCount + 1] << 8);
  
  return { averages, totals, statsVoteCount };
};

export default NFTStatsCard;
